<script setup lang="ts">
import { $t } from '@previo/core'
import { Color } from '@previo/codebooks'
import { QSelect } from 'quasar'
import { ref } from 'vue'
import { useHandleScrollQSelect } from '../../../composables/menu/handleScrollQSelect'
import PBtn from '../../btn/PBtn.vue'
import { PBtnType } from '../../btn/types'
import PSpace from '../../space/PSpace.vue'
import { PSpaceSize } from '../../space/types'
import PTextStandard from '../../typography/body/PTextStandard.vue'

const rowsPerPage = defineModel<number>('rowsPerPage')

type Props = {
  isFirstPage: boolean
  isLastPage: boolean
  statusText: string
  disabled: boolean
}

defineProps<Props>()

type Emits = {
  (e: 'start', evt: Event): void
  (e: 'prev', evt: Event): void
  (e: 'next', evt: Event): void
  (e: 'end', evt: Event): void
}

const emits = defineEmits<Emits>()

const qSelectComponent = ref<InstanceType<typeof QSelect>>()
const handleScrollQSelect = useHandleScrollQSelect(qSelectComponent)

const onClickStart = (e: Event) => {
  emits('start', e)
}

const onClickPrev = (e: Event) => {
  emits('prev', e)
}

const onClickNext = (e: Event) => {
  emits('next', e)
}

const onClickEnd = (e: Event) => {
  emits('end', e)
}
</script>

<template>
  <div class="p-flex p-items-center p-justify-end">
    <p-text-standard :text="$t('Records per page')" :color="Color.GREY_500" />:
    <p-space :mobile="PSpaceSize.SM" />
    <q-select
      ref="qSelectComponent"
      v-model="rowsPerPage"
      :options="[20, 40, 80, 160]"
      :disable="disabled"
      :autofocus="false"
      autocomplete=""
      behavior="menu"
      borderless
      @popup-show="handleScrollQSelect.subscribe"
      @popup-hide="handleScrollQSelect.unsubscribe"
    />

    <p-space :mobile="PSpaceSize.LG" />

    <p-text-standard :text="statusText" :color="Color.GREY_500" />

    <p-space :mobile="PSpaceSize.SM" />

    <p-btn
      cy="page-start"
      :color="Color.PRIMARY"
      :type="PBtnType.TERTIARY"
      :disable="disabled || isFirstPage"
      icon-end="chevrons-left"
      @click="onClickStart"
    />
    <p-btn
      cy="page-prev"
      :color="Color.PRIMARY"
      :type="PBtnType.TERTIARY"
      :disable="disabled || isFirstPage"
      icon-end="chevron-left"
      @click="onClickPrev"
    />
    <p-btn
      cy="page-next"
      :color="Color.PRIMARY"
      :type="PBtnType.TERTIARY"
      :disable="disabled || isLastPage"
      icon-end="chevron-right"
      @click="onClickNext"
    />
    <p-btn
      cy="page-end"
      :color="Color.PRIMARY"
      :type="PBtnType.TERTIARY"
      :disable="disabled || isLastPage"
      icon-end="chevrons-right"
      @click="onClickEnd"
    />
  </div>
</template>
